import React, { useEffect, useMemo, useState } from 'react';
import clinicalTemplateAdminService from './clinicalTemplateAdminService.js';

const EMPTY_FORM = {
  bodyPartTemplateId: '',
  contentCode: '',
  displayName: '',
  defaultText: '',
  allowPatientSpecificEdit: true,
  required: false,
  displayOrder: 0,
  active: true,
  adminNotes: '',
};

function normalizeTemplateToForm(template) {
  return {
    bodyPartTemplateId:
      template?.bodyPartTemplateId === null ||
      template?.bodyPartTemplateId === undefined
        ? ''
        : String(template.bodyPartTemplateId),
    contentCode: template?.contentCode || '',
    displayName: template?.displayName || '',
    defaultText: template?.defaultText || '',
    allowPatientSpecificEdit: Boolean(template?.allowPatientSpecificEdit),
    required: Boolean(template?.required),
    displayOrder:
      template?.displayOrder === null || template?.displayOrder === undefined
        ? 0
        : Number(template.displayOrder),
    active: Boolean(template?.active),
    adminNotes: template?.adminNotes || '',
  };
}

function formatDateTime(value) {
  if (!value) {
    return 'N/A';
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return parsed.toLocaleString();
}

function validateNonNegativeInteger(value, message) {
  if (value === null || value === undefined || String(value).trim() === '') {
    return message;
  }

  const numericValue = Number(value);

  if (!Number.isInteger(numericValue) || numericValue < 0) {
    return message;
  }

  return '';
}

function validateForm(form, bodyPartMap) {
  const errors = {};
  const bodyPartTemplateId = Number(form.bodyPartTemplateId);
  const bodyPart = bodyPartMap.get(bodyPartTemplateId);
  const contentCode = String(form.contentCode || '').trim();
  const displayName = String(form.displayName || '').trim();
  const defaultText = String(form.defaultText || '').trim();
  const adminNotes = String(form.adminNotes || '').trim();

  if (!Number.isInteger(bodyPartTemplateId) || bodyPartTemplateId <= 0) {
    errors.bodyPartTemplateId = 'Select a body part.';
  }

  if (!contentCode) {
    errors.contentCode = 'Content code is required.';
  } else if (contentCode.length > 120) {
    errors.contentCode = 'Content code cannot exceed 120 characters.';
  } else if (!/^[A-Za-z0-9_-]+$/.test(contentCode)) {
    errors.contentCode =
      'Content code may contain only letters, numbers, underscores, and hyphens.';
  }

  if (!displayName) {
    errors.displayName = 'Content display name is required.';
  } else if (displayName.length > 150) {
    errors.displayName = 'Content display name cannot exceed 150 characters.';
  }

  if (form.required && !form.allowPatientSpecificEdit && !defaultText) {
    errors.defaultText =
      'A required non-editable section must contain default text.';
  }

  const displayOrderError = validateNonNegativeInteger(
    form.displayOrder,
    'Display order must be a whole number of 0 or greater.'
  );

  if (displayOrderError) {
    errors.displayOrder = displayOrderError;
  }

  if (adminNotes.length > 500) {
    errors.adminNotes = 'Admin notes cannot exceed 500 characters.';
  }

  if (form.active && bodyPart && !bodyPart.active) {
    errors.active =
      'An active clinical content template cannot belong to an inactive body part.';
  }

  return errors;
}

function buildRequest(form) {
  const defaultText = String(form.defaultText || '').trim();
  const adminNotes = String(form.adminNotes || '').trim();

  return {
    bodyPartTemplateId: Number(form.bodyPartTemplateId),
    contentCode: String(form.contentCode || '').trim().toUpperCase(),
    displayName: String(form.displayName || '').trim(),
    defaultText: defaultText || null,
    allowPatientSpecificEdit: Boolean(form.allowPatientSpecificEdit),
    required: Boolean(form.required),
    displayOrder: Number(form.displayOrder),
    active: Boolean(form.active),
    adminNotes: adminNotes || null,
  };
}

function truncateText(value, maxLength = 180) {
  const text = String(value || '').trim();

  if (!text) {
    return 'No default text';
  }

  if (text.length <= maxLength) {
    return text;
  }

  return `${text.slice(0, maxLength)}...`;
}

export default function ClinicalContentTemplateAdminSection() {
  const [templates, setTemplates] = useState([]);
  const [bodyParts, setBodyParts] = useState([]);
  const [selectedBodyPartId, setSelectedBodyPartId] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [activeActionId, setActiveActionId] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [formErrors, setFormErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [editabilityFilter, setEditabilityFilter] = useState('ALL');

  const isEditing = Boolean(editingTemplate?.clinicalContentTemplateId);

  const bodyPartMap = useMemo(() => {
    const map = new Map();

    bodyParts.forEach((bodyPart) => {
      map.set(Number(bodyPart.bodyPartTemplateId), bodyPart);
    });

    return map;
  }, [bodyParts]);

  const selectedBodyPart = useMemo(
    () => bodyPartMap.get(Number(selectedBodyPartId)) || null,
    [bodyPartMap, selectedBodyPartId]
  );

  const sortedTemplates = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return [...templates]
      .filter((template) => {
        if (statusFilter === 'ACTIVE' && !template.active) {
          return false;
        }

        if (statusFilter === 'INACTIVE' && template.active) {
          return false;
        }

        if (
          editabilityFilter === 'EDITABLE' &&
          !template.allowPatientSpecificEdit
        ) {
          return false;
        }

        if (
          editabilityFilter === 'LOCKED' &&
          template.allowPatientSpecificEdit
        ) {
          return false;
        }

        if (!normalizedSearch) {
          return true;
        }

        return [
          template.clinicalContentTemplateId,
          template.contentCode,
          template.displayName,
          template.defaultText,
          template.adminNotes,
        ]
          .filter((value) => value !== null && value !== undefined)
          .join(' ')
          .toLowerCase()
          .includes(normalizedSearch);
      })
      .sort((first, second) => {
        const firstOrder = Number(first.displayOrder ?? 0);
        const secondOrder = Number(second.displayOrder ?? 0);

        if (firstOrder !== secondOrder) {
          return firstOrder - secondOrder;
        }

        return String(first.displayName || '').localeCompare(
          String(second.displayName || '')
        );
      });
  }, [templates, searchTerm, statusFilter, editabilityFilter]);

  const activeCount = useMemo(
    () => templates.filter((template) => template.active).length,
    [templates]
  );

  const requiredCount = useMemo(
    () => templates.filter((template) => template.required).length,
    [templates]
  );

  useEffect(() => {
    loadBodyParts();
  }, []);

  useEffect(() => {
    if (selectedBodyPartId) {
      loadTemplates(selectedBodyPartId);
    } else {
      setTemplates([]);
      setLoading(false);
    }
  }, [selectedBodyPartId]);

  const showSuccess = (message) => {
    setSuccess(message);
    window.setTimeout(() => setSuccess(''), 3500);
  };

  const loadBodyParts = async () => {
    try {
      setLoading(true);
      setLoadError('');

      const bodyPartData =
        await clinicalTemplateAdminService.getAllBodyPartTemplates();
      const normalized = Array.isArray(bodyPartData) ? bodyPartData : [];

      setBodyParts(normalized);

      if (normalized.length > 0) {
        const firstActive = normalized.find((bodyPart) => bodyPart.active);
        const initial = firstActive || normalized[0];

        setSelectedBodyPartId((current) =>
          current || String(initial.bodyPartTemplateId)
        );

        setForm((current) => ({
          ...current,
          bodyPartTemplateId:
            current.bodyPartTemplateId || String(initial.bodyPartTemplateId),
        }));
      } else {
        setLoading(false);
      }
    } catch (error) {
      setBodyParts([]);
      setTemplates([]);
      setLoadError(
        error?.message || 'Body-part configuration could not be loaded.'
      );
      setLoading(false);
    }
  };

  const loadTemplates = async (bodyPartTemplateId) => {
    try {
      setLoading(true);
      setLoadError('');

      const templateData =
        await clinicalTemplateAdminService.getAllClinicalContentTemplates(
          bodyPartTemplateId
        );

      setTemplates(Array.isArray(templateData) ? templateData : []);
    } catch (error) {
      setTemplates([]);
      setLoadError(
        error?.message ||
          'Clinical content template configuration could not be loaded.'
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setForm({
      ...EMPTY_FORM,
      bodyPartTemplateId: selectedBodyPartId || '',
    });
    setFormErrors({});
    setActionError('');
  };

  const beginEdit = (template) => {
    setEditingTemplate(template);
    setForm(normalizeTemplateToForm(template));
    setSelectedBodyPartId(String(template.bodyPartTemplateId));
    setFormErrors({});
    setActionError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateFormField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));

    setFormErrors((current) => {
      if (!current[field]) {
        return current;
      }

      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const handleFormBodyPartChange = (value) => {
    const bodyPart = bodyPartMap.get(Number(value));

    setForm((current) => ({
      ...current,
      bodyPartTemplateId: value,
      active:
        current.active && bodyPart && !bodyPart.active ? false : current.active,
    }));

    setSelectedBodyPartId(value);
    setFormErrors({});
    setActionError('');
  };

  const submitForm = async (event) => {
    event.preventDefault();

    const errors = validateForm(form, bodyPartMap);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      setActionError('Correct the highlighted fields before saving.');
      return;
    }

    try {
      setSaving(true);
      setActionError('');

      const request = buildRequest(form);

      if (isEditing) {
        await clinicalTemplateAdminService.updateClinicalContentTemplate(
          editingTemplate.clinicalContentTemplateId,
          request
        );
        showSuccess('Clinical content template updated.');
      } else {
        await clinicalTemplateAdminService.createClinicalContentTemplate(
          request
        );
        showSuccess('Clinical content template created.');
      }

      const bodyPartId = String(request.bodyPartTemplateId);
      setSelectedBodyPartId(bodyPartId);
      setEditingTemplate(null);
      setForm({
        ...EMPTY_FORM,
        bodyPartTemplateId: bodyPartId,
      });
      setFormErrors({});
      setActionError('');
      await loadTemplates(bodyPartId);
    } catch (error) {
      setActionError(
        error?.message || 'Clinical content template could not be saved.'
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (template) => {
    const nextActive = !template.active;
    const bodyPart = bodyPartMap.get(Number(template.bodyPartTemplateId));

    if (nextActive && bodyPart && !bodyPart.active) {
      setActionError(
        `"${bodyPart.displayName}" is inactive. Activate the body part before activating this content template.`
      );
      return;
    }

    if (
      nextActive &&
      template.required &&
      !template.allowPatientSpecificEdit &&
      !String(template.defaultText || '').trim()
    ) {
      setActionError(
        'A required non-editable clinical content template cannot be activated without default text.'
      );
      return;
    }

    if (!nextActive) {
      const confirmed = window.confirm(
        `Deactivate "${template.displayName}"? Historical clinical notes will keep their existing content template reference.`
      );

      if (!confirmed) {
        return;
      }
    }

    try {
      setActiveActionId(template.clinicalContentTemplateId);
      setActionError('');

      await clinicalTemplateAdminService.setClinicalContentTemplateActive(
        template.clinicalContentTemplateId,
        nextActive
      );

      showSuccess(
        `Clinical content template ${nextActive ? 'activated' : 'deactivated'}.`
      );

      await loadTemplates(template.bodyPartTemplateId);
    } catch (error) {
      setActionError(
        error?.message || 'Clinical content template status could not be updated.'
      );
    } finally {
      setActiveActionId(null);
    }
  };

  return (
    <div className="clinical-template-section-layout">
      {success && (
        <div className="clinical-template-message success" role="status">
          {success}
        </div>
      )}

      {actionError && (
        <div className="clinical-template-message error" role="alert">
          {actionError}
        </div>
      )}

      <section className="clinical-template-editor-panel">
        <div className="clinical-template-editor-heading">
          <span className="clinical-template-eyebrow">
            CLINICAL CONTENT CONFIGURATION
          </span>
          <h2>
            {isEditing
              ? 'Edit Clinical Content Template'
              : 'Create Clinical Content Template'}
          </h2>
          <p>
            Configure reusable narrative sections such as Subjective,
            Assessment / Diagnosis, Patient Education, Rehab Potential,
            Contraindications, Frequency / Duration, and Plan.
          </p>
        </div>

        <form onSubmit={submitForm}>
          <div className="clinical-template-form-grid">
            <label>
              Body Part
              <select
                value={form.bodyPartTemplateId}
                onChange={(event) =>
                  handleFormBodyPartChange(event.target.value)
                }
                disabled={saving || isEditing}
              >
                <option value="">Select body part</option>
                {bodyParts.map((bodyPart) => (
                  <option
                    key={bodyPart.bodyPartTemplateId}
                    value={bodyPart.bodyPartTemplateId}
                  >
                    {bodyPart.displayName} ({bodyPart.bodyPartCode})
                    {!bodyPart.active ? ' - Inactive' : ''}
                  </option>
                ))}
              </select>
              {formErrors.bodyPartTemplateId && (
                <span className="clinical-template-field-error">
                  {formErrors.bodyPartTemplateId}
                </span>
              )}
            </label>

            <label>
              Content Code
              <input
                type="text"
                value={form.contentCode}
                onChange={(event) =>
                  updateFormField('contentCode', event.target.value)
                }
                maxLength={120}
                disabled={saving || isEditing}
                placeholder="ASSESSMENT_DIAGNOSIS"
              />
              <small>
                Stable internal identifier. It cannot be changed after
                creation.
              </small>
              {formErrors.contentCode && (
                <span className="clinical-template-field-error">
                  {formErrors.contentCode}
                </span>
              )}
            </label>

            <label>
              Display Name
              <input
                type="text"
                value={form.displayName}
                onChange={(event) =>
                  updateFormField('displayName', event.target.value)
                }
                maxLength={150}
                disabled={saving}
                placeholder="Assessment / Diagnosis"
              />
              {formErrors.displayName && (
                <span className="clinical-template-field-error">
                  {formErrors.displayName}
                </span>
              )}
            </label>

            <label>
              Display Order
              <input
                type="number"
                min="0"
                step="1"
                value={form.displayOrder}
                onChange={(event) =>
                  updateFormField('displayOrder', event.target.value)
                }
                disabled={saving}
              />
              {formErrors.displayOrder && (
                <span className="clinical-template-field-error">
                  {formErrors.displayOrder}
                </span>
              )}
            </label>
          </div>

          <label className="clinical-template-full-width-field">
            Default Clinical Wording
            <textarea
              rows={8}
              value={form.defaultText}
              onChange={(event) =>
                updateFormField('defaultText', event.target.value)
              }
              disabled={saving}
              placeholder="Optional reusable clinical wording..."
            />
            <small>
              This field intentionally has no frontend length limit because the
              backend stores it as TEXT. A required section that does not allow
              patient-specific editing must contain default text.
            </small>
            {formErrors.defaultText && (
              <span className="clinical-template-field-error">
                {formErrors.defaultText}
              </span>
            )}
          </label>

          <div className="clinical-template-checkbox-grid">
            <label className="clinical-template-checkbox-field">
              <input
                type="checkbox"
                checked={form.allowPatientSpecificEdit}
                onChange={(event) =>
                  updateFormField(
                    'allowPatientSpecificEdit',
                    event.target.checked
                  )
                }
                disabled={saving}
              />
              <span>
                <strong>Allow patient-specific editing</strong>
                <small>
                  Clinicians may change the final wording for the individual
                  patient.
                </small>
              </span>
            </label>

            <label className="clinical-template-checkbox-field">
              <input
                type="checkbox"
                checked={form.required}
                onChange={(event) =>
                  updateFormField('required', event.target.checked)
                }
                disabled={saving}
              />
              <span>
                <strong>Required</strong>
                <small>
                  The section normally must be complete before finalization.
                </small>
              </span>
            </label>

            <label className="clinical-template-checkbox-field">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(event) =>
                  updateFormField('active', event.target.checked)
                }
                disabled={saving}
              />
              <span>
                <strong>Active</strong>
                <small>Offer this template on new clinical notes.</small>
              </span>
            </label>
          </div>

          {formErrors.active && (
            <span className="clinical-template-field-error">
              {formErrors.active}
            </span>
          )}

          <label className="clinical-template-full-width-field">
            Admin / Manager Notes
            <textarea
              rows={3}
              value={form.adminNotes}
              onChange={(event) =>
                updateFormField('adminNotes', event.target.value)
              }
              maxLength={500}
              disabled={saving}
              placeholder="Optional configuration notes..."
            />
            {formErrors.adminNotes && (
              <span className="clinical-template-field-error">
                {formErrors.adminNotes}
              </span>
            )}
          </label>

          <div className="clinical-template-form-actions">
            <button type="submit" disabled={saving}>
              {saving
                ? 'Saving...'
                : isEditing
                  ? 'Save Clinical Content Template'
                  : 'Create Clinical Content Template'}
            </button>

            {isEditing && (
              <button
                type="button"
                className="clinical-template-secondary-button"
                onClick={resetForm}
                disabled={saving}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </section>

      <section className="clinical-template-editor-panel clinical-template-list-panel">
        <div className="clinical-template-editor-heading clinical-template-editor-heading-row">
          <div>
            <span className="clinical-template-eyebrow">
              CONFIGURED CLINICAL CONTENT
            </span>
            <h2>Clinical Content Templates</h2>
            <p>
              {selectedBodyPart
                ? `${selectedBodyPart.displayName}: ${templates.length} total · ${activeCount} active · ${requiredCount} required`
                : 'Select a body part to view its clinical content configuration.'}
            </p>
          </div>

          <button
            type="button"
            className="clinical-template-secondary-button"
            onClick={() =>
              selectedBodyPartId && loadTemplates(selectedBodyPartId)
            }
            disabled={
              !selectedBodyPartId ||
              loading ||
              saving ||
              activeActionId !== null
            }
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {loadError && (
          <div className="clinical-template-message error" role="alert">
            {loadError}
          </div>
        )}

        <div
          className="clinical-template-list-toolbar"
          style={{
            gridTemplateColumns:
              '250px minmax(240px, 1fr) 190px 210px',
          }}
        >
          <select
            value={selectedBodyPartId}
            onChange={(event) => {
              setSelectedBodyPartId(event.target.value);
              if (!isEditing) {
                setForm((current) => ({
                  ...current,
                  bodyPartTemplateId: event.target.value,
                }));
              }
              setActionError('');
            }}
          >
            <option value="">Select body part</option>
            {bodyParts.map((bodyPart) => (
              <option
                key={bodyPart.bodyPartTemplateId}
                value={bodyPart.bodyPartTemplateId}
              >
                {bodyPart.displayName} ({bodyPart.bodyPartCode})
                {!bodyPart.active ? ' - Inactive' : ''}
              </option>
            ))}
          </select>

          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search code, section name, wording, notes..."
            disabled={!selectedBodyPartId}
          />

          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            disabled={!selectedBodyPartId}
          >
            <option value="ALL">All statuses</option>
            <option value="ACTIVE">Active only</option>
            <option value="INACTIVE">Inactive only</option>
          </select>

          <select
            value={editabilityFilter}
            onChange={(event) => setEditabilityFilter(event.target.value)}
            disabled={!selectedBodyPartId}
          >
            <option value="ALL">All editing modes</option>
            <option value="EDITABLE">Patient-editable</option>
            <option value="LOCKED">Template wording locked</option>
          </select>
        </div>

        {!selectedBodyPartId ? (
          <div className="clinical-template-empty-state">
            Select a body part to manage its clinical content templates.
          </div>
        ) : loading ? (
          <div className="clinical-template-empty-state">
            Loading clinical content templates...
          </div>
        ) : sortedTemplates.length === 0 ? (
          <div className="clinical-template-empty-state">
            No clinical content templates match the current filter.
          </div>
        ) : (
          <div className="clinical-template-table-wrap">
            <table className="clinical-template-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Section</th>
                  <th>Default Wording</th>
                  <th>Patient Edit</th>
                  <th>Required</th>
                  <th>Status</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedTemplates.map((template) => (
                  <tr key={template.clinicalContentTemplateId}>
                    <td>{template.displayOrder}</td>
                    <td>
                      <strong>{template.displayName}</strong>
                      <small className="clinical-template-table-subtext">
                        {template.contentCode}
                      </small>
                      {template.adminNotes && (
                        <small className="clinical-template-table-subtext">
                          {template.adminNotes}
                        </small>
                      )}
                    </td>
                    <td>
                      <span title={template.defaultText || ''}>
                        {truncateText(template.defaultText)}
                      </span>
                    </td>
                    <td>
                      {template.allowPatientSpecificEdit ? 'Allowed' : 'Locked'}
                    </td>
                    <td>{template.required ? 'Yes' : 'No'}</td>
                    <td>
                      <span
                        className={`clinical-template-status ${
                          template.active ? 'active' : 'inactive'
                        }`}
                      >
                        {template.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {formatDateTime(template.updatedAt)}
                      <small className="clinical-template-table-subtext">
                        Staff {template.updatedByStaffId ?? 'N/A'}
                      </small>
                    </td>
                    <td>
                      <div className="clinical-template-row-actions">
                        <button
                          type="button"
                          className="clinical-template-secondary-button"
                          onClick={() => beginEdit(template)}
                          disabled={saving || activeActionId !== null}
                        >
                          Edit
                        </button>

                        <button
                          type="button"
                          className={
                            template.active
                              ? 'clinical-template-danger-button'
                              : 'clinical-template-success-button'
                          }
                          onClick={() => toggleActive(template)}
                          disabled={
                            saving ||
                            activeActionId ===
                              template.clinicalContentTemplateId
                          }
                        >
                          {activeActionId ===
                          template.clinicalContentTemplateId
                            ? 'Updating...'
                            : template.active
                              ? 'Deactivate'
                              : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
